<div class="cv-wizard">
  <lib-headline [headline]="'cvImport.title' | translate"></lib-headline>
  @if (targetName) {
    <p class="cv-wizard__target">
      {{ 'cvImport.targetLabel' | translate }} <strong>{{ targetName }}</strong>
    </p>
  }

  <mat-stepper #stepper [linear]="true" class="cv-wizard__stepper">
    <mat-step
      [completed]="hasDraft"
      [label]="'cvImport.steps.upload' | translate"
    >
      <div class="cv-wizard__step">
        <input
          #cvFileInput
          type="file"
          accept=".docx,.pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
          class="cv-wizard__input"
          (change)="onFileSelected($event)"
        />

        <div
          class="cv-wizard__dropzone"
          [class.cv-wizard__dropzone--selected]="selectedFile"
          role="button"
          tabindex="0"
          [attr.aria-label]="'cvImport.selectFile' | translate"
          (click)="cvFileInput.click()"
          (keydown.enter)="cvFileInput.click()"
          (keydown.space)="cvFileInput.click(); $event.preventDefault()"
        >
          <mat-icon class="cv-wizard__dropzone-icon">
            {{ selectedFile ? 'description' : 'upload_file' }}
          </mat-icon>
          <div class="cv-wizard__dropzone-texts">
            @if (selectedFile) {
              <span class="cv-wizard__dropzone-file">{{
                selectedFile.name
              }}</span>
            } @else {
              <span class="cv-wizard__dropzone-prompt">{{
                'cvImport.selectFile' | translate
              }}</span>
              <span class="cv-wizard__dropzone-hint">{{
                'cvImport.formatHint' | translate
              }}</span>
            }
          </div>
          @if (selectedFile && uploadState !== 'loading') {
            <button
              type="button"
              class="cv-wizard__dropzone-remove"
              (click)="removeFile($event, cvFileInput)"
              [attr.aria-label]="'cvImport.removeFile' | translate"
            >
              <mat-icon>delete</mat-icon>
            </button>
          }
        </div>

        <div class="cv-wizard__language">
          <span class="cv-wizard__language-label" id="cvImportLanguageLabel">
            {{ 'cvImport.language.label' | translate }}
          </span>
          <mat-button-toggle-group
            aria-labelledby="cvImportLanguageLabel"
            hideSingleSelectionIndicator
            [value]="selectedLanguage"
            [disabled]="uploadState === 'loading'"
            (change)="setLanguage($event.value)"
          >
            @for (language of languageOptions; track language) {
              <mat-button-toggle [value]="language">
                {{ 'languages.' + language | translate }}
              </mat-button-toggle>
            }
          </mat-button-toggle-group>
        </div>

        <div class="cv-wizard__actions">
          <button
            mat-flat-button
            color="primary"
            type="button"
            (click)="startExtraction()"
            [disabled]="!selectedFile || uploadState === 'loading'"
          >
            @if (uploadState === 'loading') {
              <mat-spinner
                class="cv-wizard__spinner"
                diameter="18"
              ></mat-spinner>
              {{ 'cvImport.extracting' | translate }}
            } @else {
              {{ 'cvImport.extractButton' | translate }}
            }
          </button>
        </div>
      </div>
    </mat-step>

    @for (section of sections; track section.key) {
      <mat-step [completed]="true" [label]="section.titleKey | translate">
        <div class="cv-wizard__step">
          <app-cv-section-review [section]="section"></app-cv-section-review>

          <div class="cv-wizard__actions cv-wizard__actions--split">
            <button mat-button type="button" matStepperPrevious>
              {{ 'cvImport.back' | translate }}
            </button>
            <button
              mat-flat-button
              color="primary"
              type="button"
              matStepperNext
            >
              {{ 'cvImport.next' | translate }}
            </button>
          </div>
        </div>
      </mat-step>
    }

    <mat-step [label]="'cvImport.steps.summary' | translate">
      <div class="cv-wizard__step">
        <h3 class="cv-wizard__summary-title">
          {{ 'cvImport.summary.title' | translate }}
        </h3>
        <ul class="cv-wizard__summary">
          @for (section of sections; track section.key) {
            <li class="cv-wizard__summary-row">
              <span>{{ section.titleKey | translate }}</span>
              <span class="cv-wizard__summary-count">
                {{ acceptedCount(section) }}/{{ section.entries.length }}
              </span>
            </li>
          }
        </ul>

        @if (hasInvalidAccepted) {
          <p class="cv-wizard__warning">
            <mat-icon>error_outline</mat-icon>
            {{ 'cvImport.summary.invalid' | translate }}
          </p>
        } @else if (hasTooManyEntries) {
          <p class="cv-wizard__warning">
            <mat-icon>error_outline</mat-icon>
            {{
              'cvImport.summary.tooManyEntries'
                | translate: { max: maxSectionEntries }
            }}
          </p>
        } @else if (totalAccepted === 0) {
          <p class="cv-wizard__hint">
            {{ 'cvImport.summary.nothingSelected' | translate }}
          </p>
        } @else {
          <p class="cv-wizard__hint">
            {{
              'cvImport.summary.willWrite' | translate: { count: totalAccepted }
            }}
          </p>
        }

        <div class="cv-wizard__actions cv-wizard__actions--split">
          <button
            mat-button
            type="button"
            matStepperPrevious
            [disabled]="applying"
          >
            {{ 'cvImport.back' | translate }}
          </button>
          <div class="cv-wizard__actions-right">
            <button
              mat-button
              type="button"
              (click)="cancel()"
              [disabled]="applying"
            >
              {{ 'general.cancel' | translate }}
            </button>
            <button
              mat-flat-button
              color="primary"
              type="button"
              (click)="apply()"
              [disabled]="!canApply"
            >
              @if (applying) {
                <mat-spinner
                  class="cv-wizard__spinner"
                  diameter="18"
                ></mat-spinner>
                {{ 'cvImport.applying' | translate }}
              } @else {
                {{ 'cvImport.applyButton' | translate }}
              }
            </button>
          </div>
        </div>
      </div>
    </mat-step>
  </mat-stepper>

  <app-modal
    [visible]="showResultModal"
    [title]="'cvImport.result.title'"
    [showButtons]="false"
    [showClose]="true"
    [contentTemplate]="applyResultTpl"
    (cancel)="finish()"
  ></app-modal>

  <ng-template #applyResultTpl>
    @if (applyResult) {
      <div class="cv-wizard__result">
        @if (applyResult.needsReview) {
          <p class="cv-wizard__warning">
            <mat-icon>error_outline</mat-icon>
            {{ 'cvImport.result.needsReview' | translate }}
          </p>
        }
        @if (hasSkipped) {
          <p class="cv-wizard__hint">
            {{ 'cvImport.result.skippedIntro' | translate }}
          </p>
          <ul class="cv-wizard__result-list">
            @if (applyResult.skippedSkills.length) {
              <li>
                <strong>{{ 'cvImport.result.skippedSkills' | translate }}:</strong>
                {{ applyResult.skippedSkills.join(', ') }}
              </li>
            }
            @if (applyResult.skippedBranches.length) {
              <li>
                <strong>{{ 'cvImport.result.skippedBranches' | translate }}:</strong>
                {{ applyResult.skippedBranches.join(', ') }}
              </li>
            }
            @if (applyResult.skippedSchwerpunkte.length) {
              <li>
                <strong>{{ 'cvImport.result.skippedSchwerpunkte' | translate }}:</strong>
                {{ applyResult.skippedSchwerpunkte.join(', ') }}
              </li>
            }
            @if (applyResult.skippedDuplicates > 0) {
              <li>
                {{
                  'cvImport.result.skippedDuplicates'
                    | translate: { count: applyResult.skippedDuplicates }
                }}
              </li>
            }
          </ul>
        }

        <div class="cv-wizard__actions cv-wizard__result-actions">
          <button
            mat-flat-button
            color="primary"
            type="button"
            (click)="finish()"
          >
            {{
              (applyResult.needsReview
                ? 'cvImport.result.correctButton'
                : 'cvImport.result.toProfileButton'
              ) | translate
            }}
          </button>
        </div>
      </div>
    }
  </ng-template>
</div>
